import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import logger from '@/lib/logging';
// Define the shape of your settings
export type MaxWidthOption = '640px' | '720px' | '1080px' | 'Full';
export type AiProvider = 'gemini' | 'chatgpt' | 'anthropic';

interface AppSettings {
    showGrid: boolean;
    gridSize: number;
    goniometerColor: string;
    goniometerPivotColor: string;
    goniometerThickness: number;
    analysisThreshold: number;
    maxWidth: MaxWidthOption;
    aiProvider: AiProvider;
    geminiApiKey: string;
    chatGptApiKey: string;
    anthropicApiKey: string;
    assessmentDateTime: string;
}

// Define the shape of the context value
interface SettingsContextType {
    settings: AppSettings;
    setSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
}

// Default settings
const defaultSettings: AppSettings = {
    showGrid: true,
    gridSize: 3,
    goniometerColor: '#ffc800', // A vibrant yellow
    goniometerPivotColor: '#ffffff', // A clean white
    goniometerThickness: 4,
    analysisThreshold: 20,
    maxWidth: '720px',
    aiProvider: 'gemini',
    geminiApiKey: '',
    chatGptApiKey: '',
    anthropicApiKey: '',
    assessmentDateTime: new Date().toISOString().slice(0, 16),
};

// Create the context
const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Create a provider component
export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<AppSettings>(() => {
        try {
            const savedSettings = localStorage.getItem('appSettings');
            return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
        } catch (error) {
            logger.error("Could not parse settings from localStorage", error);
            return defaultSettings;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('appSettings', JSON.stringify(settings));
        } catch (error) {
            logger.error("Could not save settings to localStorage", error);
        }
    }, [settings]);

    return (
        <SettingsContext.Provider value={{ settings, setSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};

// Create a custom hook for easy access to the context
export const useSettings = (): SettingsContextType => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
